import axios, { AxiosError } from 'axios';
import {
  IconData,
  IconFilters,
  ApiResponse,
} from '../types/pageBuilder';

const API_BASE_URL = 'https://patrixglobal.com/backend/api';

/**
 * Icon statistics response
 */
interface IconStats {
  total: number;
  by_type: Record<string, number>;
  by_category: Record<string, number>;
  custom: number;
}

/**
 * Icon with usage information
 */
interface IconWithUsage extends IconData {
  usage: {
    items: number;
    statistics: number;
    total: number;
  };
}

/**
 * Service for managing icon library
 */
class IconsService {
  private token: string | null;

  constructor() {
    this.token = localStorage.getItem('admin_token');
  }

  /**
   * Get auth headers for API requests
   */
  private getHeaders() {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.token}`,
    };
  }

  /**
   * Get icon library statistics
   */
  async getIconStats(): Promise<IconStats> {
    try {
      const response = await axios.get<ApiResponse<IconStats>>(
        `${API_BASE_URL}/admin/icons/stats`,
        {
          headers: this.getHeaders(),
          withCredentials: true,
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to fetch icon statistics');
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.error || 'Failed to fetch icon statistics');
      }
      throw error;
    }
  }

  /**
   * Get all icons with optional filters
   */
  async getIcons(filters?: IconFilters): Promise<{ icons: IconData[]; count: number }> {
    try {
      const params = new URLSearchParams();

      if (filters?.category) params.append('category', filters.category);
      if (filters?.type) params.append('type', filters.type);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const response = await axios.get<ApiResponse<{ icons: IconData[]; count: number }>>(
        `${API_BASE_URL}/admin/icons?${params.toString()}`,
        {
          headers: this.getHeaders(),
          withCredentials: true,
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to fetch icons');
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.error || 'Failed to fetch icons');
      }
      throw error;
    }
  }

  /**
   * Get icons grouped by category
   */
  async getIconsByCategory(): Promise<Record<string, IconData[]>> {
    try {
      const response = await axios.get<ApiResponse<Record<string, IconData[]>>>(
        `${API_BASE_URL}/admin/icons/grouped`,
        {
          headers: this.getHeaders(),
          withCredentials: true,
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to fetch grouped icons');
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.error || 'Failed to fetch grouped icons');
      }
      throw error;
    }
  }

  /**
   * Get a single icon with usage information
   */
  async getIcon(iconId: number): Promise<IconWithUsage> {
    try {
      const response = await axios.get<ApiResponse<IconWithUsage>>(
        `${API_BASE_URL}/admin/icons/${iconId}`,
        {
          headers: this.getHeaders(),
          withCredentials: true,
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to fetch icon');
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.error || 'Failed to fetch icon');
      }
      throw error;
    }
  }

  /**
   * Upload/create a custom icon
   */
  async uploadIcon(iconData: FormData): Promise<IconData> {
    try {
      const response = await axios.post<ApiResponse<IconData>>(
        `${API_BASE_URL}/admin/icons`,
        iconData,
        {
          headers: {
            'Authorization': `Bearer ${this.token}`,
            // Don't set Content-Type for FormData - axios will set it with boundary
          },
          withCredentials: true,
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to upload icon');
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.error || 'Failed to upload icon');
      }
      throw error;
    }
  }

  /**
   * Create an icon from data (not file upload)
   */
  async createIcon(iconData: Partial<IconData>): Promise<IconData> {
    try {
      const response = await axios.post<ApiResponse<IconData>>(
        `${API_BASE_URL}/admin/icons`,
        iconData,
        {
          headers: this.getHeaders(),
          withCredentials: true,
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to create icon');
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.error || 'Failed to create icon');
      }
      throw error;
    }
  }

  /**
   * Update an icon
   */
  async updateIcon(iconId: number, updates: Partial<IconData>): Promise<IconData> {
    try {
      const response = await axios.put<ApiResponse<IconData>>(
        `${API_BASE_URL}/admin/icons/${iconId}`,
        updates,
        {
          headers: this.getHeaders(),
          withCredentials: true,
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to update icon');
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.error || 'Failed to update icon');
      }
      throw error;
    }
  }

  /**
   * Delete an icon
   */
  async deleteIcon(iconId: number): Promise<void> {
    try {
      const response = await axios.delete<ApiResponse<void>>(
        `${API_BASE_URL}/admin/icons/${iconId}`,
        {
          headers: this.getHeaders(),
          withCredentials: true,
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete icon');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.error || 'Failed to delete icon');
      }
      throw error;
    }
  }

  /**
   * Search icons by name
   */
  async searchIcons(query: string): Promise<IconData[]> {
    const result = await this.getIcons({ search: query, limit: 50 });
    return result.icons;
  }
}

// Export a singleton instance
export const iconsService = new IconsService();
